import { AppSection, NomObjectClass } from '../../../core/constants';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ViewChild,
  ViewContainerRef,
  computed,
  inject,
} from '@angular/core';
import { Dashboard, DataObject, Feed } from '../../../core/models/core.models';
import { Observable, tap } from 'rxjs';
import {
  selectCoreAppSection,
  selectCurrentObject,
  selectCurrentObjectEditMode,
  selectUser,
  selectUserAccessLevelGlobalAdmin,
  selectUserAccessLevelName,
  selectUserCompany,
} from '../../../core/state';

import { AppFeatures } from '../../../core/app.features';
import { AppState } from 'src/app/core/state/app.state';
import { DropdownAlertsComponent } from './dropdown-alerts/dropdown-alerts.component';
import { DropdownNotificationsComponent } from './dropdown-notifications/dropdown-notifications.component';
import { DropdownUserComponent } from './dropdown-user/dropdown-user.component';
import { ObjectCreatorService } from '../../../core/services/object-creator.service';
import { PresentationService } from '../../../core/services/presentation.service';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { CommandPaletteService } from 'src/app/shell/services/command-palette.service';
import { KeyboardShortcutService } from 'src/app/shell/services/keyboard-shortcut.service';
import { UserFacadeService } from '@services/user-facade.service';
import { environment } from '@env/environment';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserPreferencesService } from '../../../core/services/user-preferences.service';

import {
  HeaderTools,
  HeaderToolsService,
} from '../../../core/services/header-tools.service';
import { injectUserAlertsCountQuery } from 'src/app/api/queries/alert.queries';
import {
  NotificationsScope,
  injectNotificationsCountQuery,
} from 'src/app/api/queries/notification.queries';
import { NgpPopoverTrigger, NgpPopover } from 'ng-primitives/popover';
import { SuiPopoverTriggerDirective, SuiPopoverDirective } from '@sui/popover';
import { CompanyBadgeComponent } from '../../../shared/components/company-badge/company-badge.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { NgpFocusTrap } from 'ng-primitives/focus-trap';
import { CompanySwitcherComponent } from '../../../shared/components/company-switcher/company-switcher.component';
import { SearchSuggestComponent } from './search-suggest/search-suggest.component';
import { SuiButtonComponent } from '@sui/button';
import { SuiTooltipDirective } from '@sui/tooltip';
import { NgpMenuTrigger, NgpMenu, NgpMenuItem } from 'ng-primitives/menu';
import {
  SuiMenuTriggerDirective,
  SuiMenuComponent,
  SuiMenuItemComponent,
} from '@sui/menu';
import {
  NgpRovingFocusGroup,
  NgpRovingFocusItem,
} from 'ng-primitives/roving-focus';
import { BreadcrumbComponent } from './breadcrumb/breadcrumb.component';
import { FeedViewsComponent } from './feed-views/feed-views.component';
import { DateRangePickerComponent } from '../../../shared/components/date-range-picker/date-range-picker.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgpPopoverTrigger,
    SuiPopoverTriggerDirective,
    CompanyBadgeComponent,
    FaIconComponent,
    NgpFocusTrap,
    NgpPopover,
    SuiPopoverDirective,
    CompanySwitcherComponent,
    RouterLink,
    SearchSuggestComponent,
    SuiButtonComponent,
    SuiTooltipDirective,
    NgpMenuTrigger,
    SuiMenuTriggerDirective,
    NgpRovingFocusGroup,
    NgpMenu,
    SuiMenuComponent,
    NgpRovingFocusItem,
    NgpMenuItem,
    SuiMenuItemComponent,
    BreadcrumbComponent,
    FeedViewsComponent,
    DateRangePickerComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class HeaderComponent {
  public appName: string = environment.appName;
  NomObjectClass = NomObjectClass;
  public isMenuCollapsed: boolean = true;
  activeCompany: any;
  accessLevel: any;
  currentObject$: Observable<DataObject>;
  currentUser$: Observable<any>;

  currentObjectEditMode$: Observable<boolean>;
  isGlobalAdmin$: Observable<boolean>;

  /**
   * The two badge counts. Owned here rather than by a root service because the
   * header is the only reader, and `gcTime: Infinity` keeps both entries alive while
   * Presentation Mode has it unmounted - so a socket notification arriving then still
   * increments the badge.
   */
  private readonly user = inject(Store<AppState>).selectSignal(selectUser);

  private readonly countsScope = computed<NotificationsScope | null>(() => {
    const user = this.user();

    return user?.id
      ? { userId: user.id, companyId: user.active_company_id }
      : null;
  });

  private readonly alertsCountQuery = injectUserAlertsCountQuery(
    this.countsScope,
  );
  private readonly notificationsCountQuery = injectNotificationsCountQuery(
    this.countsScope,
  );

  readonly countAlerts = computed(() => this.alertsCountQuery.data() ?? 0);
  readonly countNotifications = computed(
    () => this.notificationsCountQuery.data() ?? 0,
  );

  /** Screen-registered header controls (date range / data-view toggle). */
  readonly headerTools$: Observable<HeaderTools | null> =
    inject(HeaderToolsService).currentTools$;

  private destroyRef = inject(DestroyRef);
  private readonly userPreferences = inject(UserPreferencesService);

  @ViewChild('alertsDropdownContainer', { read: ViewContainerRef })
  alertsContainer: ViewContainerRef;

  @ViewChild('notificationsDropdownContainer', { read: ViewContainerRef })
  notificationsContainer: ViewContainerRef;

  @ViewChild('userDropdownContainer', { read: ViewContainerRef })
  userContainer: ViewContainerRef;

  appSection: AppSection = 'analytics';
  activeMenu: 'alerts' | 'notifications' | 'user' | 'none' = 'none';

  AppComponents = AppFeatures;

  protected readonly commandPalette = inject(CommandPaletteService);
  private readonly shortcuts = inject(KeyboardShortcutService);
  private paletteShortcut: symbol | null = null;

  constructor(
    private store: Store<AppState>,
    private objectCreatorService: ObjectCreatorService,
    public userFacade: UserFacadeService,
    protected router: Router,
    private presentationService: PresentationService,
  ) {
    this.currentObject$ = this.store.select(selectCurrentObject);
    this.currentUser$ = this.store.select(selectUser);
    this.currentObjectEditMode$ = this.store.select(
      selectCurrentObjectEditMode,
    );
    this.isGlobalAdmin$ = this.store.select(selectUserAccessLevelGlobalAdmin);
    // Ctrl+/ (Cmd+/ on macOS) opens the command palette for Global Admins and
    // up (WAP-1692); the chord is registered only while the level allows it.
    this.isGlobalAdmin$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isGlobalAdmin) => this.togglePaletteShortcut(isGlobalAdmin));
    this.destroyRef.onDestroy(() => this.togglePaletteShortcut(false));

    this.store
      .select(selectCoreAppSection)
      .pipe(
        tap((section) => {
          this.appSection = section;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUserCompany)
      .pipe(
        tap((company) => {
          this.activeCompany = company?.name;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.store
      .select(selectUserAccessLevelName)
      .pipe(
        tap((accessLevelName) => {
          this.accessLevel = accessLevelName;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  public ngOnInit(): void {}

  showAlerts(): void {
    if (this.activeMenu === 'alerts') {
      this.alertsContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'alerts';

    this.alertsContainer.clear();
    this.notificationsContainer.clear();
    this.userContainer.clear();

    const componentRef = this.alertsContainer.createComponent(
      DropdownAlertsComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.alertsContainer.clear();
      if (this.activeMenu === 'alerts') {
        this.activeMenu = 'none';
      }
    };
  }

  showNotifications(): void {
    if (this.activeMenu === 'notifications') {
      this.notificationsContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'notifications';

    this.notificationsContainer.clear();
    this.alertsContainer.clear();
    this.userContainer.clear();

    const componentRef = this.notificationsContainer.createComponent(
      DropdownNotificationsComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.notificationsContainer.clear();
      if (this.activeMenu === 'notifications') {
        this.activeMenu = 'none';
      }
    };
  }

  showUserSettings(): void {
    if (this.activeMenu === 'user') {
      this.userContainer.clear();
      this.activeMenu = 'none';
      return;
    }

    this.activeMenu = 'user';

    this.userContainer.clear();
    this.notificationsContainer.clear();
    this.alertsContainer.clear();

    const componentRef = this.userContainer.createComponent(
      DropdownUserComponent,
    );

    componentRef.instance.closeMethod = () => {
      this.userContainer.clear();
      if (this.activeMenu === 'user') {
        this.activeMenu = 'none';
      }
    };
  }

  createNewFeed() {
    this.objectCreatorService.createNewFeed();
  }

  createNewWorkspace() {
    this.objectCreatorService.createNewWorkspace();
  }

  createNewDashboard() {
    this.objectCreatorService.createNewDashboard();
  }

  createNewDimension() {
    this.objectCreatorService.createNewDimension();
  }

  createNewUseCase() {
    this.objectCreatorService.createNewUseCase();
  }

  toggleDarkMode() {
    this.userPreferences.toggleDarkMode();
  }

  showPresentationMode(object: Feed | Dashboard) {
    if (object.object_class_id === NomObjectClass.DASHBOARD) {
      this.presentationService.showDashboardPresentation(object.id);
    }
  }

  private togglePaletteShortcut(enabled: boolean): void {
    if (enabled && !this.paletteShortcut) {
      this.paletteShortcut = this.shortcuts.register(
        { key: '/', ctrl: true },
        () => this.commandPalette.open(),
      );
    } else if (!enabled && this.paletteShortcut) {
      this.shortcuts.unregister(this.paletteShortcut);
      this.paletteShortcut = null;
    }
  }
}
